import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { LOGO_URL } from "@/lib/brand";

const navItems = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/inventory", label: "Inventory", testId: "nav-inventory" },
  { to: "/financing", label: "Financing", testId: "nav-financing" },
  { to: "/videos", label: "Videos", testId: "nav-videos" },
  { to: "/about", label: "Our Mission", testId: "nav-about" },
  { to: "/contact", label: "Contact", testId: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "sticky top-0 z-40 w-full transition-all duration-300 bg-accent text-accent-foreground",
        scrolled ? "shadow-md border-b border-black/20" : "border-b border-transparent"
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <img
            src={LOGO_URL}
            alt="Pit Stop Motors — Idaho"
            className="h-12 md:h-14 w-auto select-none"
            draggable={false}
          />
          <span className="hidden sm:flex flex-col leading-none ml-1">
            <span className="overline text-[10px]">Used Cars · Idaho</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testId}
              className={({ isActive }) =>
                cn(
                  "text-sm tracking-wide transition-colors",
                  isActive
                    ? "text-accent-foreground font-semibold"
                    : "text-accent-foreground/70 hover:text-accent-foreground"
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/inventory"
            data-testid="nav-cta-browse"
            className="ml-2 inline-flex items-center justify-center bg-primary text-primary-foreground px-5 py-2 rounded-sm text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Browse Cars
          </Link>
        </nav>

        <button
          className="md:hidden p-2 text-accent-foreground"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-black/20 bg-accent" data-testid="mobile-nav">
          <div className="flex flex-col p-6 gap-4">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${n.testId}`}
                className={({ isActive }) =>
                  cn(
                    "text-base py-1 transition-colors",
                    isActive ? "text-accent-foreground font-semibold" : "text-accent-foreground/70"
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href="tel:+12087898284"
              className="mt-2 inline-flex items-center justify-center border border-accent-foreground/30 py-2.5 rounded-sm text-sm font-medium"
              data-testid="mobile-nav-call"
            >
              Call 208-789-8284
            </a>
            <Link
              to="/inventory"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center bg-primary text-primary-foreground py-2.5 rounded-sm text-sm font-medium"
              data-testid="mobile-nav-cta-browse"
            >
              Browse Cars
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
